<template>
  <div id="app">
    <nav class="navbar app-navbar mb-3" v-if="!isMapRoute">
      <div class="container-fluid flex-wrap gap-2 px-0">
        <loc-link class="navbar-brand clickable d-flex align-items-center gap-2 my-0 me-3" :to="'/'">
          <img
            src="./assets/images/mkart_logo.jpg"
            width="36"
            height="36"
            alt="mKart"
            class="rounded"
          />
          <span class="d-flex flex-column lh-1 gap-1">
            <strong>mKart</strong>
            <small class="text-muted brand-subtitle">Backoffice</small>
          </span>
        </loc-link>

        <div class="nav nav-pills gap-1 user-roles" v-if="auth.status.loggedIn">
          <loc-link :rltag="'button'" :to="'/user'" :class="cabBtnClass('user')">{{ $t('nav.user') }}</loc-link>
          <loc-link
            :rltag="'button'"
            :to="'/provider'"
            :class="cabBtnClass('provider')"
          >{{ $t('nav.publisher') }}</loc-link>
          <loc-link
            :rltag="'button'"
            :to="'/distributor'"
            :class="cabBtnClass('distributor')"
          >{{ $t('nav.distributor') }}</loc-link>
          <loc-link
            v-if="hasFullAccess"
            :rltag="'button'"
            :to="'/distributor2'"
            :class="cabBtnClass('distributor2', 'teleapi_full_access')"
          >{{ $t('nav.manager') }}</loc-link>
          <loc-link
            v-if="hasFullAccess"
            :rltag="'button'"
            :to="'/order-manager'"
            :class="cabBtnClass('order-manager', 'teleapi_full_access')"
          >{{ $t('nav.orders') }}</loc-link>
          <!-- <button @click="switchTo('reseller')" :class="cabBtnClass('reseller')">{{ $t('auth.user.email') }}</button> -->
        </div>

        <div class="d-flex align-items-center gap-3 ms-auto">
          <span class="d-flex align-items-center gap-2" v-if="auth.status.loggedIn">
            <span
              class="d-none d-lg-inline text-muted small text-truncate account-email"
            >{{auth.user.email}}</span>
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="logout"
            >{{ $t('main.nav.logout') }}</button>
          </span>
          <div class="btn-group btn-group-sm" role="group" :aria-label="$t('nav.language')">
            <router-link
              v-for="item in locales"
              :key="item.code"
              :to="localeTo(item)"
              :class="localeBtnClass(item)"
              :aria-current="item.code === locale.code ? 'true' : null"
            >{{ item.code.toUpperCase() }}</router-link>
          </div>
        </div>
      </div>
    </nav>

    <div v-if="!isMapRoute" class="container app-content flex-grow-1">
      <div v-if="alert.message">{{alert.message}}</div>
      <router-view></router-view>
    </div>
    <div v-else class="flex-grow-1">
      <router-view></router-view>
    </div>

    <footer v-if="!isMapRoute" class="app-footer">
      <div class="container py-3">
        <p class="mb-0 small text-muted text-center">
          © 2020–{{ copyrightYear }} Geophone International OY, Finland
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { SUPPORTED_LOCALES } from "./localization/locales";

export default {
  name: "app",
  data() {
    return {
      locales: SUPPORTED_LOCALES
    };
  },
  computed: {
    alert() {
      return this.$store.state.alert;
    },
    auth() {
      return this.$store.state.auth;
    },
    locale() {
      return this.$store.getters.locale;
    },
    isMapRoute() {
      return this.$route.name === "map";
    },
    // Current route without the locale prefix, so it can be re-prefixed.
    unprefixedPath() {
      const base = this.locale.base;
      const path = this.$route.path;
      return base && path.startsWith(base) ? path.substring(base.length) : path;
    },
    hasFullAccess() {
      return (
        this.auth.status.loggedIn &&
        this.auth.user.roles.some(r => r.role === "teleapi_full_access")
      );
    },
    copyrightYear() {
      return new Date().getFullYear();
    }
  },
  watch: {
    $route() {
      // clear alert on location change
      this.$store.dispatch("alert/clear");
    },
    locale: {
      immediate: true,
      handler(value) {
        document.documentElement.lang = value.code || "en";
      }
    }
  },
  methods: {
    logout() {
      this.$store.dispatch("auth/logout");
    },
    switchTo(val) {
      this.$router.push("/" + val);
    },
    localeTo(item) {
      const path = this.unprefixedPath === "/" ? "" : this.unprefixedPath;
      return item.base + path || "/";
    },
    localeBtnClass(item) {
      return item.code === this.locale.code
        ? "btn btn-secondary"
        : "btn btn-outline-secondary";
    },
    // `cab` is the first path segment, `role` the grant it needs when they differ.
    cabBtnClass(cab, role = cab) {
      if (!this.auth.user.roles.some(r => r.role === role)) {
        return "nav-link text-muted";
      }
      return this.unprefixedPath.split("/")[1] === cab
        ? "nav-link active"
        : "nav-link";
    }
  }
};
</script>

<style scoped>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-navbar {
  background-color: #e3f2fd;
  padding: 0.5rem 10px;
  border-bottom: 1px solid rgba(13, 110, 253, 0.15);
}

.user-roles {
  --bs-nav-link-padding-x: 0.65rem;
  --bs-nav-link-padding-y: 0.25rem;
  --bs-nav-link-font-size: 0.875rem;
}

.brand-subtitle {
  font-size: 0.6rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.account-email {
  max-width: 14rem;
}

.clickable {
  cursor: pointer;
}

.app-content {
  padding-left: 10px;
  padding-right: 10px;
}

.app-footer {
  margin-top: auto;
  background-color: #e3f2fd;
  border-top: 1px solid rgba(48, 97, 173, 0.15);
}
</style>